import { ImageDescriptor, loader } from '@play-co/timestep-core';
import LRUCache from '@play-co/timestep-core/lib/cache/LRUCache';
import {
  OffscreenRenderContext,
  releaseRenderTarget,
} from '@play-co/timestep-core/lib/platforms/browser/RenderContext';
import {
  ImageView,
  MaskView,
  TintFilter,
  View,
} from '@play-co/timestep-core/ui';
import gsap from 'gsap';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import { GENERIC_AVATAR } from 'src/replicant/profile/assets';
import { AvatarFrameId } from 'src/replicant/profile/frames';
import { BasicAvatarFrame } from './BasicAvatarFrame';
import StateObserver from 'src/StateObserver';

const assets = {
  border: 'assets/ui/shared/frames/avatar_border.png',
  shadow: 'assets/ui/shared/frames/avatar_shadow.png',
};

type DropShadowOption = {
  opacity: number;
  offsetX: number;
  offsetY: number;
};

type CommonOpts = {
  superview?: View;
  tag?: string;
  image?: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  borderThickness?: number;
  borderTint?: string;
  dropShadow?: Boolean | Partial<DropShadowOption>;
  frame?: AvatarFrameId;
  // What size of rendered to texture image should be used
  // It will be also used as cache key
  sourceWidth?: number;
  sourceHeight?: number;
  centerOnOrigin?: boolean;
  canHandleEvents?: boolean;
};

const fadeDuration = 0.15;

export type AvatarOpts =
  | (CommonOpts & {
      onClick: undefined;
      tracking: undefined;
    })
  | (CommonOpts & {
      onClick?: () => Promise<void>;
      tracking?: {
        feature: string;
        $subFeature: string;
      };
    });

// Cache of rendered avatar textures. Each entry holds one offscreen
// framebuffer, so the cache is bounded. An entry evicted from the LRU is only
// released once no attached Avatar draws it: the ImageView keeps drawing
// `src._texture` directly, so releasing early would show a deleted texture.
const CACHE_LIMIT = 128;

type CacheEntry = {
  image: ImageDescriptor;
  // Attached Avatars currently drawing this texture
  refs: number;
};

const CACHE = new LRUCache<CacheEntry>(CACHE_LIMIT);
// Evicted from the LRU but still drawn by at least one Avatar
const EVICTED = new Map<string, CacheEntry>();

function findEntry(key: string): CacheEntry | undefined {
  return CACHE.find(key)?.value ?? EVICTED.get(key);
}

function releaseEntry(entry: CacheEntry) {
  releaseRenderTarget(entry.image.src as OffscreenRenderContext);
}

/**
 * Take a reference on an existing entry. Returns false if the texture was
 * already released, in which case the caller has to render again.
 */
function retain(key: string): boolean {
  const entry = findEntry(key);
  if (!entry) return false;
  entry.refs++;
  return true;
}

/**
 * Drop a reference. Releases the texture once it left the LRU and nobody
 * draws it anymore.
 */
function release(key: string) {
  const entry = findEntry(key);
  if (!entry) return;
  entry.refs--;
  if (entry.refs === 0 && EVICTED.delete(key)) {
    releaseEntry(entry);
  }
}

/**
 * Get the entry for `key`, rendering it when missing, and take a reference.
 */
function acquire(key: string, render: () => ImageDescriptor): ImageDescriptor {
  let entry = CACHE.get(key) ?? EVICTED.get(key);
  if (!entry) {
    entry = { image: render(), refs: 0 };
    const evicted = CACHE.put(key, entry);
    if (evicted) {
      if (evicted.value.refs > 0)
        EVICTED.set(String(evicted.key), evicted.value);
      else releaseEntry(evicted.value);
    }
  }
  entry.refs++;
  return entry.image;
}

// One shared view stack for every avatar render. A MaskView allocates an
// offscreen framebuffer that is only released on resize, so a MaskView per
// render would leak one framebuffer per unique avatar.
type RenderRoot = {
  view: View;
  border: ImageView;
  mask: MaskView;
  photo: ImageView;
};

let renderRoot: RenderRoot | undefined;

function getRenderRoot(): RenderRoot {
  if (renderRoot) return renderRoot;

  const view = new View();

  const border = new ImageView({
    superview: view,
    image: assets.border,
    width: defaultSize,
    height: defaultSize,
  });

  const mask = new MaskView({
    superview: view,
    mask: GENERIC_AVATAR,
    width: defaultSize - defaultBorderThickness * 2,
    height: defaultSize - defaultBorderThickness * 2,
    x: defaultBorderThickness,
    y: defaultBorderThickness,
  });

  const photo = new ImageView({
    superview: mask,
    width: defaultSize,
    height: defaultSize,
    x: -defaultBorderThickness,
    y: -defaultBorderThickness,
  });

  renderRoot = { view, border, mask, photo };
  return renderRoot;
}

const defaultSize = 130;
const defaultBorderThickness = 7;

const baseAssetSize = 140;
const shadowAssetSize = 150;
const shadowSizeRatio = shadowAssetSize / baseAssetSize;

export class Avatar extends ButtonScaleView {
  // Border padding
  private borderThickness: number;
  private borderTint?: string;

  // Rendered masked view
  private avatar: ImageView;
  private overlay?: ImageView;
  private frame?: BasicAvatarFrame;

  // Cache rendered texture prefix key
  private sourceWidth: number;
  private sourceHeight: number;
  private pendingImageUpdate: string | null = null;
  // Cache key of the rendered texture `avatar` currently shows
  private renderedKey: string | null = null;
  // Whether this view holds a reference on `renderedKey`
  private holdsRef = false;
  // Whether `updateImage` is waiting for its image to load
  private loading = false;

  constructor(opts: AvatarOpts) {
    const {
      superview,
      image,
      x = 0,
      y = 0,
      width = defaultSize,
      height = defaultSize,
      borderThickness = defaultBorderThickness,
      borderTint,
      dropShadow,
      frame = 'default',
      sourceWidth = defaultSize,
      sourceHeight = defaultSize,
      tracking = {
        feature: 'avatar',
        $subFeature: 'avatar-tap',
      },
      onClick,
      tag,
      centerOnOrigin = true,
      canHandleEvents = !!onClick,
    } = opts;

    super({
      superview,
      x,
      y,
      width,
      height,
      onClick: onClick,
      tracking,
      tag,
      centerOnOrigin,
      canHandleEvents,
    });

    this.sourceWidth = sourceWidth;
    this.sourceHeight = sourceHeight;
    this.borderThickness = borderThickness;
    this.borderTint = borderTint;

    // Drop shadow
    if (dropShadow) {
      const shadowOpts = (
        typeof dropShadow === 'boolean' ? {} : dropShadow
      ) as DropShadowOption;
      this.createDropShadow(shadowOpts);
    }

    // Avatar
    this.avatar = new ImageView({
      superview: this,
      image: GENERIC_AVATAR,
      width,
      height,
      canHandleEvents,
    });

    // Only attached views hold a reference on their rendered texture
    this.on('ViewRemoved', this.releaseRendered);
    this.on('ViewAdded', this.retainRendered);

    // Update avatar image, if provided
    if (image) {
      void this.updateImage(image);
    }

    // Update frame on avatar, if provided
    if (frame) {
      this.setFrame(frame);
    }
  }

  private createDropShadow(opts: DropShadowOption) {
    const { opacity = 0.25, offsetX = 0, offsetY = 4 } = opts;
    const shadowWidth = this.style.width * shadowSizeRatio;
    const shadowHeight = this.style.height * shadowSizeRatio;
    const baseOffset = (this.style.width - shadowWidth) / 2;
    new ImageView({
      superview: this,
      image: assets.shadow,
      width: shadowWidth,
      height: shadowHeight,
      x: baseOffset + offsetX,
      y: baseOffset + offsetY,
      opacity,
    });
  }

  private getCacheKey(image: string): string {
    // We generate a source size image, not a width and height based image
    // We do that to cache more usage cases of avatar
    return `${image}_${this.sourceWidth}x${this.sourceHeight}_${this.borderThickness}_${this.borderTint}`;
  }

  private releaseRendered = () => {
    if (!this.holdsRef || !this.renderedKey) return;
    this.holdsRef = false;
    release(this.renderedKey);
  };

  private retainRendered = () => {
    if (this.holdsRef) return;
    if (this.renderedKey && retain(this.renderedKey)) {
      this.holdsRef = true;
      return;
    }
    // No texture, or it was released while detached. Render again unless a
    // load is already in flight; that load renders on its own when it lands.
    this.renderedKey = null;
    if (this.pendingImageUpdate && !this.loading) {
      void this.updateImage(this.pendingImageUpdate);
    }
  };

  /**
   * Show the rendered texture for `image`, taking a reference on it
   */
  private setRendered(image: string) {
    const cacheKey = this.getCacheKey(image);
    this.releaseRendered();
    this.renderedKey = cacheKey;
    this.holdsRef = true;
    this.avatar.setImage(acquire(cacheKey, () => this.renderAvatarView(image)));
  }

  /**
   * Render avatar view to simple image to reduce performance impact
   */
  private renderAvatarView(image: string): ImageDescriptor {
    const width = this.sourceWidth;
    const height = this.sourceHeight;
    const borderThickness = this.borderThickness;
    const borderTint = this.borderTint;

    const { view, border, mask, photo } = getRenderRoot();

    border.updateOpts({ width, height });
    if (borderTint) {
      border.setFilter(new TintFilter(borderTint));
    } else {
      border.removeFilter();
    }

    mask.updateOpts({
      width: width - borderThickness * 2,
      height: height - borderThickness * 2,
      x: borderThickness,
      y: borderThickness,
    });

    photo.setImage(image);
    photo.updateOpts({
      width,
      height,
      x: -borderThickness,
      y: -borderThickness,
    });

    // Render to texture. No id: Timestep would keep its own unbounded copy.
    return view.style.renderToImage(undefined, {
      width,
      height,
      offsetX: 0,
      offsetY: 0,
    });
  }

  /**
   * Update avatar image
   *
   * @param id - Cache prefix id, use feature_name
   * @param image - Player photo
   */
  public async updateImage(image: string, animate = false) {
    // If image starts with an internal asset id, convert to full URL if possible
    if (image.startsWith('avatar-')) {
      const newImage = StateObserver.replicant.getChatbotAssetUrl(image);
      if (newImage) image = newImage;
    }

    // Set current image update.
    this.pendingImageUpdate = image;

    // Temporary set default picture
    this.releaseRendered();
    this.renderedKey = null;
    this.avatar.setImage(GENERIC_AVATAR);

    // Keep default if image not set
    if (!image) return;

    // Preload image - if successful, it should return a valid descriptor
    this.loading = true;
    const imageDesc = await loader.loadImage(image);

    // Another update already happened, its own load owns `loading` now.
    if (this.pendingImageUpdate !== image) return;
    this.loading = false;

    // Preload failed, keep the default picture.
    if (!imageDesc) return;

    // Detached while loading. ViewAdded re-runs updateImage from pendingImageUpdate.
    if (!this.getApp()) return;

    // Show overlay if animation is requested
    if (animate) this.showOverlay();

    // Render avatar to texture and show it
    this.setRendered(image);

    // Fade out the overlay if animation is requested
    if (animate) this.fadeOutOverlay();
  }

  private showOverlay() {
    // Skip overlay creation if it already exists
    if (this.overlay) {
      // Reset overlay properties
      this.overlay.updateOpts({ visible: true, opacity: 1 });
      return;
    }

    this.overlay = new ImageView({
      superview: this,
      image: assets.border,
      width: this.style.width,
      height: this.style.height,
    });
  }

  private async fadeOutOverlay() {
    // Skip if overlay doesn't exist
    if (!this.overlay) return;

    await gsap.to(this.overlay.style, {
      duration: fadeDuration,
      opacity: 0,
      ease: 'quad.in',
      overwrite: true,
      onComplete: () => {
        if (!this.overlay) return;
        this.overlay.style.visible = false;
      },
    });
  }

  public setFrame(id: AvatarFrameId) {
    if (!this.frame) {
      this.frame = new BasicAvatarFrame({ avatar: this, id });
    } else {
      this.frame.setFrame(id);
    }
  }
}
